/**
 * 실력 점검 · 화계 문항의 보기를 만든다.
 *
 * 처음 만든 방식은 **오답을 전부 다른 단어에서** 가져왔다. 그래서 문항이 이렇게 나왔다.
 *
 *   조금만 〈기다리면〉
 *   ① 아이  ② 멀어집니다  ③ 기다려야 합니다  ④ 기다리면
 *
 * 한국어를 몰라도 풀 수 있다 — '기다리-'로 시작하는 둘만 남기고, 그중 안 바뀐 ④를
 * 빼면 끝이다. words.exam_form 이 단어마다 하나뿐이라 **같은 단어의 다른 형태**가
 * 보기에 들어갈 자리가 없었던 것이 원인이다.
 *
 * 그래서 오답을 세 종류로 만든다.
 *   ① 자막에 나온 그대로 (기다리면)        — 실제로 답안지에 이렇게 쓴다
 *   ② 거기에 요만 붙인 것 (기다리면요)     — 해요체는 시험 답안에서 여전히 틀리다
 *   ③ 다른 단어의 같은 종류 형태 (멀어집니다)
 *
 * ①②가 같은 어간이라 "생김새가 닮은 것"으로는 좁혀지지 않고, ③이 같은 종류라
 * "격식체처럼 생긴 것"으로도 좁혀지지 않는다. 두 판단을 다 해야 답이 나온다.
 */

/**
 * 뜻풀이에 붙은 한국어 예시를 걷어낸다.
 *
 * TOPIK 어휘 목록의 뜻풀이 78개가 `store (e.g., 옷 가게)` 꼴이다. 어휘 문항은
 * 「가게」를 보여주고 뜻을 고르게 하는데, 보기에 그 단어가 그대로 적혀 있으면
 * 한국어를 몰라도 답이 보인다. 괄호 안에 한국어가 있으면 예시 부분만 잘라낸다 —
 * `to give (honorific form, e.g., 어머니께 …)` 의 'honorific form' 은 남긴다.
 */
export function cleanMeaning(meaning: string): string {
  return meaning
    .replace(/\(([^)]*)\)/g, (full, inner: string) => {
      if (!/[가-힣]/.test(inner)) return full
      const kept = inner
        .split(/e\.g\.,?/i)[0]
        .replace(/[,;\s]+$/, '')
        .trim()
      return kept ? `(${kept})` : ''
    })
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/** `약속합시다 / 약속하겠습니다` 처럼 두 형태를 담은 값에서 앞의 것만 쓴다 */
export function firstForm(examForm: string): string {
  return examForm.split('/')[0].trim()
}

/**
 * 종결형 변환인지(기다리면 → 기다려야 합니다), 낱말 교체인지(애 → 아이).
 * 둘을 섞으면 모양만 보고 답을 고를 수 있다 — 명사 문항에 '멀어집니다'가 끼는 식이다.
 */
export function isFormalForm(form: string): boolean {
  return /(니다|니까|십시오|시오)[.!?]?$/.test(form.trim())
}

/**
 * 자막 토큰의 서법(敍法).
 *
 * exam_form 은 **단어마다 하나**뿐이라, 같은 단어라도 문장에서 어떻게 활용됐는지에
 * 따라 답이 달라진다는 것을 담지 못한다. 실제로 이렇게 틀린 답을 내놓고 있었다.
 *
 *   말로는 〈지운다〉 해도   →  지웁니다      ✓
 *   이만 너를 〈지울게〉     →  지웁니다      ✗  '-ㄹ게'는 의도다 → 지우겠습니다
 *   조금만 〈기다리면〉      →  기다려야 합니다 ✗  '-면'은 연결어미다 → 바꿀 수 없다
 *
 * 서법이 맞는 것만 문항으로 쓴다. 틀린 답을 가르치느니 문항이 적은 편이 낫다.
 */
export type Mood = 'connective' | 'intent' | 'imperative' | 'plain'

/**
 * 그 음절의 종성이 ㄹ 인가.
 *
 * '-ㄹ게'는 정규식 `/ㄹ게$/` 로 잡히지 않는다 — 한글은 자모가 음절 안에 합쳐져
 * 있어서 '울'과 'ㄹ'은 다른 글자다. 종성을 직접 계산한다.
 */
const JONG_RIEUL = 8
function endsWithRieul(syllable: string): boolean {
  const code = syllable.charCodeAt(0) - 0xac00
  return code >= 0 && code < 11172 && code % 28 === JONG_RIEUL
}

export function tokenMood(token: string): Mood {
  const t = token.replace(/[.!?~…]+$/, '').trim()
  // 연결어미 — 문장이 끝나지 않는다. 종결형으로 바꿔 넣을 자리가 없다.
  if (/(면|며|고|서|지만|는데|은데|니까|다가|거나|든지|러|려고)$/.test(t)) return 'connective'
  // 의도·약속 (-ㄹ게 · -ㄹ래 · -ㄹ까) — 격식체는 '-겠습니다'다.
  // 앞 음절의 종성이 ㄹ 이어야 한다: 지울게 ○ / 이따 보게 ✕
  const last = t.slice(-1)
  const prev = t.slice(-2, -1)
  if (['게', '래', '까'].includes(last) && prev && endsWithRieul(prev)) return 'intent'
  // 명령·요청 — 격식체는 '-십시오'다
  if (/(줘|다오|어라|아라|거라)$/.test(t)) return 'imperative'
  return 'plain'
}

/** exam_form 이 어떤 서법을 전제하고 쓰였는지 */
export function formMood(examForm: string): Mood {
  const f = firstForm(examForm).replace(/[.!?]+$/, '').trim()
  if (/겠습니다$/.test(f)) return 'intent'
  if (/(십시오|시오)$/.test(f)) return 'imperative'
  return 'plain'
}

/**
 * 이 자리에서 이 exam_form 으로 바꾸라고 물어도 되는가.
 *
 * 낱말 교체형(애 → 아이, 꼭 → 반드시)은 활용과 무관하므로 서법을 따지지 않는다 —
 * 어디에 있든 그 낱말을 바꾸면 된다.
 */
export function convertible(token: string, examForm: string, sentence?: string): boolean {
  if (!isFormalForm(examForm)) return true
  const mood = tokenMood(token)
  if (mood === 'connective') return false
  /*
   * 호격이 있으면 명령문으로 본다. '얘들아 밥 먹어'의 '먹어'는 생김새로는 평서형과
   * 구별되지 않아 '먹습니다'를 답이라고 하게 되는데, 실제로는 '드십시오'다.
   * 문장 하나로 서법을 가리는 데 한계가 있으니, 애매하면 문항으로 쓰지 않는다.
   */
  // 호격은 거의 언제나 맨 앞에 온다. 아무 데서나 '야'를 찾으면 '얼마나 기다려야'의
  // 끝까지 호격으로 잡힌다 — 문장 첫 낱말만 본다.
  if (sentence && /^[가-힣]{1,4}(들아|야)\s/.test(sentence.trim()) && formMood(examForm) === 'plain') {
    return false
  }
  return mood === formMood(examForm)
}

/**
 * 자막의 말에 요만 붙인 형태. 해요체는 말할 때는 공손하지만 **시험 답안에서는 틀리다** —
 * 학습자가 실제로 많이 하는 실수라 오답으로 값이 있다.
 * 이미 종결어미로 끝나 붙일 수 없는 형태면 null.
 */
export function politeVariant(token: string): string | null {
  const t = token.replace(/[.!?~…]+$/, '').trim()
  if (!t) return null
  if (/(요|다|까|오|죠|네)$/.test(t)) return null
  return `${t}요`
}

/**
 * 보기 네 개. 순서는 고정이고(정답이 맨 앞) 섞는 것은 호출부가 한다 —
 * 여기서 섞으면 테스트가 흔들린다.
 *
 * @param pool 다른 단어들의 exam_form (정답과 같은 종류만 걸러 쓴다)
 * @param take pool 에서 n 개를 고르는 방법. 실제로는 무작위, 테스트에서는 앞에서부터.
 * @returns 보기 4개. 재료가 모자라면 null — 문항을 만들지 않는다.
 */
export function registerOptions(
  answer: string,
  token: string,
  pool: string[],
  take: (arr: string[], n: number) => string[],
): string[] | null {
  const formal = isFormalForm(answer)

  const sameStem: string[] = []
  if (token.trim() && token.trim() !== answer) sameStem.push(token.trim())
  if (formal) {
    const polite = politeVariant(token)
    if (polite && polite !== answer && !sameStem.includes(polite)) sameStem.push(polite)
  }

  const others = [...new Set(pool.map(firstForm))].filter(
    (o) => o !== answer && !sameStem.includes(o) && isFormalForm(o) === formal,
  )

  const need = 4 - 1 - sameStem.length
  if (need < 0) return [answer, ...sameStem.slice(0, 3)]
  const picked = take(others, need)
  if (picked.length < need) return null

  return [answer, ...sameStem, ...picked]
}
